import { collection, getDocs, query, orderBy, where, doc, getDoc, addDoc, Timestamp, updateDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { isEmploymentType, normalizeEmploymentType } from '@/constants/employmentTypes';
import { User } from '../types';

function mapFirestoreUserDoc(d: { id: string; data: () => Record<string, unknown> }): User {
  const data = d.data();
  const rawEt = data.employmentType;
  return {
    id: d.id,
    name: (data.name as string) || (data.email as string) || 'User',
    email: data.email as string,
    ...data,
    employmentType:
      rawEt !== undefined && rawEt !== null && rawEt !== ''
        ? normalizeEmploymentType(rawEt)
        : undefined,
  } as User;
}

export const usersService = {
  /**
   * Nutzer org-übergreifend per E-Mail finden (z. B. um ihn einer anderen Organisation hinzuzufügen).
   * Firestore vergleicht exakt, daher Original- und Kleinschreibung abfragen.
   */
  async findUserByEmail(email: string): Promise<User | null> {
    const trimmed = email.trim();
    if (!trimmed) return null;
    const variants = Array.from(new Set([trimmed, trimmed.toLowerCase()]));
    const snap = await getDocs(query(collection(db, 'users'), where('email', 'in', variants)));
    return snap.empty ? null : mapFirestoreUserDoc(snap.docs[0]);
  },

  async getUsers(orgId?: string): Promise<User[]> {
    try {
      const resolvedOrgId = orgId || (await this.getCurrentUserOrgId());
      console.log('👥 Loading users for orgId:', resolvedOrgId);
      
      const base = collection(db, 'users');
      
      // Try with orderBy first
      try {
        const q = resolvedOrgId
          ? query(base, where('orgId', '==', resolvedOrgId), orderBy('name'))
          : query(base, orderBy('name'));
        const snap = await getDocs(q);
        const users = snap.docs.map((d) => mapFirestoreUserDoc(d));
        
        console.log(`✅ Loaded ${users.length} users:`, users.map(u => ({ id: u.id, name: u.name })));
        return users;
      } catch (orderByError) {
        // Fallback without orderBy if it fails
        console.warn('⚠️ orderBy failed, trying without...', orderByError);
        const q = resolvedOrgId
          ? query(base, where('orgId', '==', resolvedOrgId))
          : base;
        const snap = await getDocs(q);
        const users = snap.docs.map((d) => mapFirestoreUserDoc(d));
        
        console.log(`✅ Loaded ${users.length} users (no orderBy):`, users.map(u => ({ id: u.id, name: u.name })));
        return users;
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
      return [];
    }
  },

  async getCurrentUserProfile(): Promise<User | null> {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    const rawEt = data.employmentType;
    return {
      id: snap.id,
      ...data,
      employmentType:
        rawEt !== undefined && rawEt !== null && rawEt !== ''
          ? normalizeEmploymentType(rawEt)
          : undefined,
    } as User;
  },

  async getCurrentUserOrgId(): Promise<string | null> {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // Create a minimal profile with personal org
      const profile = {
        id: uid,
        name: auth.currentUser?.displayName || 'User',
        email: auth.currentUser?.email || '',
        role: 'admin',
        specialties: [],
        currentLeadCount: 0,
        orgId: uid
      } as any;
      await setDoc(ref, profile);
      return uid;
    }
    const data = snap.data() as any;
    if (data.orgId) return data.orgId as string;
    // Fallback: assign personal org equal to uid
    await updateDoc(ref, { orgId: uid, role: data.role || 'admin' });
    return uid;
  },

  async inviteUserByEmail(email: string, role: 'admin' | 'manager' | 'sales_rep' = 'sales_rep'): Promise<string> {
    const orgId = await this.getCurrentUserOrgId();
    if (!orgId) throw new Error('Missing orgId for current user');
    const ref = await addDoc(collection(db, 'invites'), {
      email: email.toLowerCase(),
      orgId,
      role,
      status: 'pending',
      createdAt: Timestamp.now().toDate(),
      createdBy: auth.currentUser?.uid || null
    });
    return ref.id;
  },

  async acceptInvite(inviteId: string): Promise<void> {
    const uid = auth.currentUser?.uid;
    const email = auth.currentUser?.email?.toLowerCase();
    if (!uid || !email) throw new Error('User must be signed in to accept invite');

    const inviteRef = doc(db, 'invites', inviteId);
    const snap = await getDoc(inviteRef);
    if (!snap.exists()) throw new Error('Invite not found');
    const invite = snap.data() as any;
    if (invite.email !== email) throw new Error('Invite email does not match current user');

    // attach org to user profile and set role
    const userRef = doc(db, 'users', uid);
    const profileSnap = await getDoc(userRef);
    if (!profileSnap.exists()) throw new Error('User profile not found');
    
    // Update user with orgId and role. Firestore rules only allow this
    // self-change when acceptedInviteId points at a matching pending invite.
    await updateDoc(userRef, {
      orgId: invite.orgId,
      role: invite.role || 'sales_rep',
      acceptedInviteId: inviteId
    });

    // Mark invite as accepted
    await updateDoc(inviteRef, { 
      status: 'accepted', 
      acceptedAt: Timestamp.now().toDate(), 
      acceptedBy: uid 
    });
    
    console.log(`✅ User ${uid} accepted invite and joined organization ${invite.orgId}`);
  },

  // Generate invite link for sharing
  getInviteLink(inviteId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}?invite=${inviteId}`;
  },

  async setUserRole(userId: string, role: 'admin' | 'manager' | 'sales_rep'): Promise<void> {
    const orgId = await this.getCurrentUserOrgId();
    if (!orgId) throw new Error('Missing orgId');
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { role });
  },

  async updateCurrentUserProfile(updates: Partial<User> & Record<string, any>): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Nicht authentifiziert. Bitte erneut einloggen.');
    const allowed: Record<string, any> = {};
    if (typeof updates.name === 'string') allowed.name = updates.name;
    if (typeof updates.position === 'string') allowed.position = updates.position;
    if (typeof updates.avatar === 'string') allowed.avatar = updates.avatar;
    if (Array.isArray(updates.specialties)) allowed.specialties = updates.specialties;
    if (typeof updates.phone === 'string') allowed.phone = updates.phone;
    if (typeof updates.location === 'string') allowed.location = updates.location;
    if (typeof updates.bio === 'string') allowed.bio = updates.bio;
    if (typeof updates.timezone === 'string') allowed.timezone = updates.timezone;
    if (typeof updates.language === 'string') allowed.language = updates.language;
    allowed.updatedAt = Timestamp.now().toDate();
    const userRef = doc(db, 'users', uid);
    try {
      await updateDoc(userRef, allowed);
    } catch (error: any) {
      if (error.code === 'permission-denied') {
        throw new Error('Keine Berechtigung, das Profil zu speichern. Bitte kontaktiere den Administrator.');
      }
      if (error.code === 'not-found') {
        const orgId = await this.getCurrentUserOrgId();
        await setDoc(userRef, { ...allowed, email: auth.currentUser?.email, orgId: orgId || uid }, { merge: true });
        return;
      }
      throw new Error(`Profil konnte nicht gespeichert werden: ${error.message || 'Unbekannter Fehler'}`);
    }
  },

  async listInvites(): Promise<Array<{ id: string; email: string; orgId: string; role: string; status: string }>> {
    const orgId = await this.getCurrentUserOrgId();
    if (!orgId) return [];
    const base = collection(db, 'invites');
    const q = query(base, where('orgId', '==', orgId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
  },

  async revokeInvite(inviteId: string): Promise<void> {
    // Soft revoke by updating status
    const ref = doc(db, 'invites', inviteId);
    await updateDoc(ref, { status: 'revoked', revokedAt: Timestamp.now().toDate() });
  },

  // Admin functions for user management
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    const currentUser = await this.getCurrentUserProfile();
    
    // Super-Admin can update any user, Org-Admin can update users in their org
    if (!currentUser || (!currentUser.isSuperAdmin && currentUser.role !== 'admin')) {
      throw new Error('Only admins can update users');
    }
    
    const userRef = doc(db, 'users', userId);
    const allowedUpdates: any = {};
    
    // Only allow certain fields to be updated
    if (updates.name) allowedUpdates.name = updates.name;
    if (updates.role) allowedUpdates.role = updates.role;
    if (updates.position) allowedUpdates.position = updates.position;
    if (updates.specialties) allowedUpdates.specialties = updates.specialties;
    if (updates.email) allowedUpdates.email = updates.email;
    if (updates.employmentType !== undefined) {
      if (!isEmploymentType(updates.employmentType)) {
        throw new Error('Ungültiger Mitarbeitertyp (employmentType)');
      }
      allowedUpdates.employmentType = updates.employmentType;
    }
    if (updates.orgId !== undefined) allowedUpdates.orgId = updates.orgId; // For Super-Admin org transfers
    
    // Only Super-Admin can change isSuperAdmin status
    if (updates.isSuperAdmin !== undefined && currentUser.isSuperAdmin) {
      allowedUpdates.isSuperAdmin = updates.isSuperAdmin;
    }
    
    allowedUpdates.updatedAt = Timestamp.now().toDate();
    
    await updateDoc(userRef, allowedUpdates);
  },

  async deleteUser(userId: string): Promise<void> {
    const currentUser = await this.getCurrentUserProfile();
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only admins can delete users');
    }
    
    // Don't allow self-deletion
    if (userId === currentUser.id) {
      throw new Error('Cannot delete your own account');
    }
    
    const userRef = doc(db, 'users', userId);
    // Soft delete - just mark as inactive
    await updateDoc(userRef, { 
      status: 'inactive',
      deactivatedAt: Timestamp.now().toDate(),
      deactivatedBy: currentUser.id
    });
  },

  async reactivateUser(userId: string): Promise<void> {
    const currentUser = await this.getCurrentUserProfile();
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only admins can reactivate users');
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { 
      status: 'active',
      reactivatedAt: Timestamp.now().toDate(),
      reactivatedBy: currentUser.id
    });
  },

  async getUserStats(userId: string): Promise<any> {
    // Get user statistics for dashboard
    try {
      const stats = {
        totalLeads: 0,
        totalOpportunities: 0,
        totalTasks: 0,
        completedTasks: 0
      };
      
      // You can expand this with actual queries to count entities
      return stats;
    } catch (error) {
      console.error('Error getting user stats:', error);
      return null;
    }
  },

  // Super-Admin functions
  async getAllUsers(): Promise<User[]> {
    try {
      const currentUser = await this.getCurrentUserProfile();
      
      // Only Super-Admin can access all users
      if (!currentUser?.isSuperAdmin) {
        throw new Error('Only super admins can access all users');
      }
      
      console.log('🌍 Super-Admin loading ALL users (across all organizations)...');
      
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || data.email || 'User',
          email: data.email,
          ...data
        };
      }) as User[];
      
      console.log(`✅ Loaded ${users.length} users from all organizations`);
      
      // Sort by organization and name
      users.sort((a, b) => {
        const orgCompare = (a.orgId || '').localeCompare(b.orgId || '');
        if (orgCompare !== 0) return orgCompare;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      return users;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  },

  async isSuperAdmin(userId?: string): Promise<boolean> {
    try {
      const checkUserId = userId || auth.currentUser?.uid;
      if (!checkUserId) return false;
      
      const userDoc = await getDoc(doc(db, 'users', checkUserId));
      if (!userDoc.exists()) return false;
      
      const userData = userDoc.data() as User;
      return userData.isSuperAdmin === true;
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return false;
    }
  },

  /**
   * Bulk create invites for team onboarding
   * Used by SmartOnboardingWizard to invite multiple team members at once
   */
  async bulkCreateInvites(
    orgId: string,
    emails: string[],
    invitedBy: string,
    defaultRole: string = 'manager'
  ): Promise<{ sent: string[]; failed: string[] }> {
    const sent: string[] = [];
    const failed: string[] = [];

    for (const email of emails) {
      try {
        const trimmedEmail = email.trim().toLowerCase();
        
        // Validate email format
        if (!trimmedEmail || !trimmedEmail.includes('@')) {
          failed.push(email);
          continue;
        }

        // Create invite
        const inviteRef = collection(db, 'invites');
        await addDoc(inviteRef, {
          email: trimmedEmail,
          orgId,
          role: defaultRole,
          status: 'pending',
          createdAt: Timestamp.now().toDate(),
          createdBy: invitedBy
        });

        sent.push(trimmedEmail);
        console.log(`✅ Invite created for ${trimmedEmail}`);
      } catch (error) {
        console.error(`❌ Failed to create invite for ${email}:`, error);
        failed.push(email);
      }
    }

    console.log(`📧 Bulk invites: ${sent.length} sent, ${failed.length} failed`);
    
    return { sent, failed };
  }
};


